import React, { useMemo } from 'react';
import { Card } from 'react-bootstrap';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import './ARRGrowthChart.css';

function ARRGrowthChart({ buildingsData }) {
  const chartData = useMemo(() => {
    if (!buildingsData || buildingsData.length === 0) {
      return [];
    }

    // Filter out buildings without contract values or creation dates
    const validBuildings = buildingsData.filter(building => 
      building.contractValue && 
      building.contractValue > 0 && 
      building.createdAt
    );

    if (validBuildings.length === 0) {
      return [];
    }

    // Sort buildings by creation date
    const sortedBuildings = validBuildings.sort((a, b) => 
      new Date(a.createdAt) - new Date(b.createdAt)
    );

    // Group by month and calculate cumulative ARR
    const monthlyData = {};
    let cumulativeARR = 0;

    sortedBuildings.forEach(building => {
      const date = new Date(building.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthKey,
          newARR: 0,
          cumulativeARR: 0,
          buildingCount: 0
        };
      }
      
      monthlyData[monthKey].newARR += building.contractValue;
      monthlyData[monthKey].buildingCount += 1;
    });

    // Calculate cumulative ARR
    Object.keys(monthlyData).sort().forEach(monthKey => {
      cumulativeARR += monthlyData[monthKey].newARR;
      monthlyData[monthKey].cumulativeARR = cumulativeARR;
    });

    // Convert to array and format for chart
    return Object.values(monthlyData).map(item => {
      // Parse the month key (e.g., "2024-11") to get year and month
      const [year, month] = item.month.split('-');
      const monthIndex = parseInt(month) - 1; // Convert to 0-based index
      
      // Create month label using the original date parsing
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthLabel = `${monthNames[monthIndex]} ${year}`;
      
      return {
        ...item,
        month: monthLabel,
        monthKey: item.month, // Preserve the original month key
        cumulativeARRFormatted: `$${item.cumulativeARR.toLocaleString()}`,
        newARRFormatted: `$${item.newARR.toLocaleString()}`
      };
    });
  }, [buildingsData]);

  // After processing buildingsData, pad the data to start from January at zero ARR
  let paddedChartData = chartData;
  if (chartData.length > 0) {
    const firstMonthKey = chartData[0].monthKey;
    const [startYear] = firstMonthKey.split('-');
    const janKey = `${startYear}-01`;
    if (firstMonthKey !== janKey) {
      paddedChartData = [
        {
          month: `Jan ${startYear}`,
          monthKey: janKey,
          cumulativeARR: 0,
          newARR: 0,
          buildingCount: 0,
          cumulativeARRFormatted: '$0',
          newARRFormatted: '$0'
        },
        ...chartData
      ];
    }
  }

  if (paddedChartData.length === 0) {
    return (
      <Card className="arr-chart-card">
        <Card.Body>
          <h5 className="chart-title">ARR Growth Over Time</h5>
          <div className="no-data-message">
            <p>No contract data available to display ARR growth.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          <p className="tooltip-value">
            <span style={{ color: '#007bff' }}>Cumulative ARR:</span> {payload[0].payload.cumulativeARRFormatted}
          </p>
          <p className="tooltip-value">
            <span style={{ color: '#28a745' }}>New ARR:</span> {payload[0].payload.newARRFormatted}
          </p>
          <p className="tooltip-value">
            <span style={{ color: '#ffc658' }}>Buildings Added:</span> {payload[0].payload.buildingCount}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="arr-chart-card">
      <Card.Body>
        <h5 className="chart-title">ARR Growth Over Time</h5>
        <div className="chart-summary">
          <div className="summary-item">
            <span className="summary-label">Total ARR:</span>
            <span className="summary-value" style={{ color: '#111' }}>
              ${paddedChartData[paddedChartData.length - 1]?.cumulativeARR.toLocaleString() || '0'}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Buildings:</span>
            <span className="summary-value" style={{ color: '#111' }}>
              {paddedChartData.reduce((sum, item) => sum + item.buildingCount, 0)}
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">MoM % Growth:</span>
            <span className="summary-value" style={{
              color: (() => {
                const len = paddedChartData.length;
                if (len < 2) return '#888';
                const prev = paddedChartData[len - 2].cumulativeARR;
                const curr = paddedChartData[len - 1].cumulativeARR;
                if (prev === 0) return curr > 0 ? '#28a745' : '#888';
                if (curr > prev) return '#28a745';
                if (curr < prev) return '#dc3545';
                return '#888';
              })()
            }}>
              {(() => {
                const len = paddedChartData.length;
                if (len < 2) return '0.00%';
                const prev = paddedChartData[len - 2].cumulativeARR;
                const curr = paddedChartData[len - 1].cumulativeARR;
                if (prev === 0) return curr > 0 ? '+100.00%' : '0.00%';
                const percent = ((curr - prev) / prev) * 100;
                return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
              })()}
            </span>
          </div>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300} className="no-focus-outline">
            <AreaChart data={paddedChartData}>
              <XAxis 
                dataKey="month" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cumulativeARR"
                stackId="1"
                stroke="#007bff"
                fill="#007bff"
                fillOpacity={0.6}
                name="Cumulative ARR"
                activeDot={{ style: { outline: 'none' } }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
}

export default ARRGrowthChart; 